"use client";

import { cn } from "@ui/lib";
import type { PropsWithChildren } from "react";
import { createContext, useContext, useEffect, useRef, useState } from "react";

/**
 * CSS-only motion primitives for marketing bento widgets. No animation
 * library: an IntersectionObserver flips state once, CSS transitions do the
 * rest, and every moving class sits behind `motion-safe:` so
 * prefers-reduced-motion renders the finished state immediately.
 */

/**
 * When an element counts as "in view": as soon as any part of it is inside
 * the viewport with the bottom 15% trimmed off, i.e. once its top edge has
 * risen past 85% of the screen height.
 *
 * A fraction-of-the-element threshold (the old `0.35`) never fires for an
 * element taller than about 2.9 viewports, because 35% of it can't fit on
 * screen at once; stacked plan cards on a phone stayed invisible. A fixed
 * slice of the viewport works at any element height.
 */
export const IN_VIEW_OBSERVER_OPTIONS: IntersectionObserverInit = {
	threshold: 0,
	rootMargin: "0px 0px -15% 0px",
};

export function useInView<T extends HTMLElement>(
	options: IntersectionObserverInit = IN_VIEW_OBSERVER_OPTIONS,
) {
	const ref = useRef<T>(null);
	const [inView, setInView] = useState(false);
	const { threshold, rootMargin } = options;

	useEffect(() => {
		const el = ref.current;
		if (!el) return;
		if (typeof IntersectionObserver === "undefined") {
			setInView(true);
			return;
		}
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries.some((e) => e.isIntersecting)) {
					setInView(true);
					observer.disconnect();
				}
			},
			{ threshold, rootMargin },
		);
		observer.observe(el);
		return () => observer.disconnect();
	}, [threshold, rootMargin]);

	return { ref, inView };
}

const RevealContext = createContext(false);

/** True once the nearest Reveal ancestor has entered the viewport. */
export function useRevealed() {
	return useContext(RevealContext);
}

/**
 * Fade-and-rise entrance for a section or tile. `delay` (ms) staggers
 * siblings. Children that need to sequence their own internals (bars, count-
 * ups) read `useRevealed()` instead of observing twice.
 */
export function Reveal({
	delay = 0,
	className,
	children,
}: PropsWithChildren<{ delay?: number; className?: string }>) {
	const { ref, inView } = useInView<HTMLDivElement>();

	return (
		<RevealContext.Provider value={inView}>
			<div
				ref={ref}
				style={{ transitionDelay: `${delay}ms` }}
				className={cn(
					// min-w-0 lets grid/flex tracks shrink below content width
					// on small screens — without it long widget content drags
					// the whole page wider than the viewport.
					"min-w-0 motion-safe:transition-all motion-safe:duration-700 motion-safe:ease-out",
					inView
						? "motion-safe:translate-y-0 motion-safe:opacity-100"
						: "motion-safe:translate-y-6 motion-safe:opacity-0",
					className,
				)}
			>
				{children}
			</div>
		</RevealContext.Provider>
	);
}

function prefersReducedMotion(): boolean {
	return (
		typeof window !== "undefined" &&
		window.matchMedia("(prefers-reduced-motion: reduce)").matches
	);
}

/**
 * Tabular count-up that starts when the nearest Reveal enters the viewport
 * (or its own observer when used standalone). Reduced motion renders the
 * final value straight away.
 */
export function CountUp({
	value,
	decimals = 0,
	suffix = "",
	durationMs = 1200,
	delay = 0,
	className,
}: {
	value: number;
	decimals?: number;
	suffix?: string;
	durationMs?: number;
	delay?: number;
	className?: string;
}) {
	const revealed = useRevealed();
	const { ref, inView } = useInView<HTMLSpanElement>();
	const active = revealed || inView;
	const [display, setDisplay] = useState(0);
	const started = useRef(false);

	useEffect(() => {
		if (!active || started.current) return;
		started.current = true;
		if (prefersReducedMotion()) {
			setDisplay(value);
			return;
		}
		let raf = 0;
		let start: number | null = null;
		const tick = (now: number) => {
			if (start === null) start = now + delay;
			const t = Math.min(1, Math.max(0, (now - start) / durationMs));
			const eased = 1 - (1 - t) ** 3;
			setDisplay(value * eased);
			if (t < 1) raf = requestAnimationFrame(tick);
		};
		raf = requestAnimationFrame(tick);
		return () => cancelAnimationFrame(raf);
	}, [active, value, durationMs, delay]);

	return (
		<span ref={ref} className={cn("tabular-nums", className)}>
			{display.toFixed(decimals)}
			{suffix}
		</span>
	);
}
